export function normalizeSelectedModifiers(value) {
    if (!Array.isArray(value))
        return [];
    const unique = new Map();
    value.forEach((row) => {
        if (!row || typeof row !== 'object')
            return;
        const rec = row;
        if (typeof rec.id !== 'number' || typeof rec.name !== 'string' || typeof rec.price !== 'number')
            return;
        unique.set(rec.id, { id: rec.id, name: rec.name, price: rec.price });
    });
    return Array.from(unique.values()).sort((a, b) => a.id - b.id);
}
export function productLineId(productId, selectedModifiers) {
    const suffix = selectedModifiers.length > 0
        ? selectedModifiers
            .map((modifier) => modifier.id)
            .sort((a, b) => a - b)
            .join(',')
        : 'base';
    return `product:${productId}:${suffix}`;
}
export function parseProductRow(rec) {
    const p = rec.product;
    const q = rec.quantity;
    if (p &&
        typeof p.id === 'number' &&
        typeof p.name === 'string' &&
        typeof p.price === 'number' &&
        typeof p.imageUrl === 'string' &&
        typeof q === 'number' &&
        q > 0) {
        const selectedModifiers = normalizeSelectedModifiers(rec.selectedModifiers);
        const fallbackLineId = productLineId(p.id, selectedModifiers);
        const lineId = typeof rec.lineId === 'string' && rec.lineId.trim() ? rec.lineId : fallbackLineId;
        return {
            kind: 'product',
            lineId,
            product: {
                id: p.id,
                name: p.name,
                price: p.price,
                imageUrl: p.imageUrl,
                grams: p.grams,
                categoryId: p.categoryId,
                description: p.description,
                ingredients: p.ingredients,
                proteins: p.proteins,
                fats: p.fats,
                carbohydrates: p.carbohydrates,
                calories: p.calories,
                bonusAmount: typeof p.bonusAmount === 'number' ? p.bonusAmount : 0,
                disabledRestaurantIds: Array.isArray(p.disabledRestaurantIds) ? p.disabledRestaurantIds : [],
                modifiers: Array.isArray(p.modifiers) ? p.modifiers : [],
            },
            selectedModifiers,
            quantity: q,
        };
    }
    return null;
}
export function parseComboRow(rec) {
    const c = rec.combo;
    const q = rec.quantity;
    if (c &&
        typeof c.id === 'number' &&
        typeof c.name === 'string' &&
        typeof c.price === 'number' &&
        typeof q === 'number' &&
        q > 0) {
        const combo = {
            id: c.id,
            menuId: typeof c.menuId === 'number' ? c.menuId : 0,
            name: c.name,
            description: typeof c.description === 'string' ? c.description : '',
            price: c.price,
            ingredients: typeof c.ingredients === 'string' ? c.ingredients : '',
            grams: typeof c.grams === 'number' ? c.grams : null,
            bonusAmount: typeof c.bonusAmount === 'number' ? c.bonusAmount : 0,
            proteins: typeof c.proteins === 'number' ? c.proteins : 0,
            fats: typeof c.fats === 'number' ? c.fats : 0,
            carbohydrates: typeof c.carbohydrates === 'number' ? c.carbohydrates : 0,
            calories: typeof c.calories === 'number' ? c.calories : 0,
            sortOrder: typeof c.sortOrder === 'number' ? c.sortOrder : 0,
            imageUrl: typeof c.imageUrl === 'string' ? c.imageUrl : '',
            items: Array.isArray(c.items) ? c.items : [],
        };
        const fallbackLineId = `combo:${combo.id}`;
        const lineIdRaw = rec.lineId;
        const lineId = typeof lineIdRaw === 'string' && lineIdRaw.trim() ? lineIdRaw : fallbackLineId;
        return { kind: 'combo', lineId, combo, quantity: q };
    }
    return null;
}
export function parseItems(rows) {
    const items = [];
    for (const row of rows) {
        if (!row || typeof row !== 'object')
            continue;
        const rec = row;
        const q = rec.quantity;
        if (typeof q !== 'number' || q <= 0)
            continue;
        if (rec.kind === 'combo') {
            const comboLine = parseComboRow({
                combo: rec.combo,
                quantity: q,
                lineId: rec.lineId,
            });
            if (comboLine)
                items.push(comboLine);
            continue;
        }
        const productLine = parseProductRow({
            product: rec.product,
            quantity: q,
            selectedModifiers: rec.selectedModifiers,
            lineId: rec.lineId,
        });
        if (productLine)
            items.push(productLine);
    }
    return items;
}
export function addProductToSlice(slice, product, quantity = 1, selectedModifiers = []) {
    const normalizedModifiers = normalizeSelectedModifiers(selectedModifiers);
    const lineId = productLineId(product.id, normalizedModifiers);
    const existing = slice.items.find((it) => it.kind === 'product' && it.lineId === lineId);
    if (existing && existing.kind === 'product') {
        return {
            ...slice,
            items: slice.items.map((it) => it.kind === 'product' && it.lineId === lineId
                ? { ...it, quantity: it.quantity + quantity }
                : it),
        };
    }
    return {
        ...slice,
        items: [
            ...slice.items,
            {
                kind: 'product',
                lineId,
                product,
                selectedModifiers: normalizedModifiers,
                quantity,
            },
        ],
    };
}
export function addComboToSlice(slice, combo, quantity = 1) {
    const existing = slice.items.find((it) => it.kind === 'combo' && it.combo.id === combo.id);
    if (existing && existing.kind === 'combo') {
        return {
            ...slice,
            items: slice.items.map((it) => it.kind === 'combo' && it.combo.id === combo.id
                ? { ...it, quantity: it.quantity + quantity }
                : it),
        };
    }
    return {
        ...slice,
        items: [...slice.items, { kind: 'combo', lineId: `combo:${combo.id}`, combo, quantity }],
    };
}
export function setProductQuantityInSlice(slice, lineId, quantity) {
    if (quantity <= 0) {
        return {
            ...slice,
            items: slice.items.filter((it) => !(it.kind === 'product' && it.lineId === lineId)),
        };
    }
    return {
        ...slice,
        items: slice.items.map((it) => it.kind === 'product' && it.lineId === lineId ? { ...it, quantity } : it),
    };
}
export function setComboQuantityInSlice(slice, comboId, quantity) {
    if (quantity <= 0) {
        return {
            ...slice,
            items: slice.items.filter((it) => !(it.kind === 'combo' && it.combo.id === comboId)),
        };
    }
    return {
        ...slice,
        items: slice.items.map((it) => it.kind === 'combo' && it.combo.id === comboId ? { ...it, quantity } : it),
    };
}
